import { request } from "./client";
import type {
  NotificationFrequency,
  NotificationKind,
  Skill,
} from "../types/prisma-enums";

/**
 * The parent tier's reads and writes, one typed function each.
 *
 * 🔴 EVERY ONE SENDS `context: 'user'` AND CARRIES A REAL TOKEN. Unlike the
 * teacher tier — which 401s today because staff login is still unbuilt (OD-51) —
 * **parent login exists**: `POST /auth/parent/otp/verify` mints an access token,
 * and `shared/stores/parentSession.ts` holds it. So C1–C5 are not "wired and
 * waiting"; they work.
 *
 * ⚠️ POST, NEVER PATCH. `client.ts` types `method` as `'GET' | 'POST'` and
 * belongs to another slice. A verb the client cannot send is a route nothing
 * calls, so every write below is a POST and the backend routes match.
 *
 * 🔴 NO FIXTURES IN THIS TIER, ANYWHERE. §8.4's TODO row: *"the predecessor
 * rendered 8 hardcoded children while working endpoints sat uncalled."*
 */

export interface ParentAuth {
  token?: string;
  signal?: AbortSignal;
}

export interface ChildSummary {
  playerId: string;
  name: string;
  avatar: string | null;
  className: string | null;
  rollNumber: string | null;
}

export interface ContentSkillRow {
  skill: Skill;
  /** 🔴 A LEVEL. There is deliberately no percentage field for a content skill. */
  levelCompleted: number;
  events: number;
}

export interface MotorSkillRow {
  skill: Skill;
  /** The numerator. Its denominator is `ParentDashboard.sessionsPlayed`. */
  sessionsExercised: number;
}

export type ConsentStatus = "GRANTED" | "PENDING" | "REVOKED" | "EXPIRED";

export interface ParentDashboard {
  playerId: string;
  childName: string;
  className: string | null;
  /**
   * 🔴 THE ID BESIDE THE NAME, AND C1 NEEDS IT FOR EXACTLY ONE THING: asking
   * `GET /homework?classId=` for THIS child's section.
   *
   * Without it the parent's homework read returns every class their children
   * sit in, and a parent of two would read the elder's homework under the
   * younger's name. Narrowing the request is not the same as filtering the
   * response — the server re-checks the class against their own children, so a
   * `classId` the browser could not have been given returns nothing.
   *
   * ⚠️ NULL FOR A CHILD IN NO CLASS, which is a real state on a roster imported
   * before the sections were made.
   */
  classId: string | null;
  gamesThisWeek: number;
  sessionsThisWeek: number;
  streakDays: number;
  coins: number;
  stickers: number;
  lastPlayed: string | null;
  content: ContentSkillRow[];
  motor: MotorSkillRow[];
  /**
   * 🔴 THE DENOMINATOR OD-39 SAID NO DOCUMENT SUPPLIES. It travels with the
   * numerator so C1 can render *"exercised in 8 of 10 sessions"* rather than a
   * bare percentage — see `skillSections.ts`.
   */
  sessionsPlayed: number;
  consent: ConsentStatus;
  recordingOptOut: boolean;
}

/**
 * C6 — one child's attendance.
 *
 * 🔴 THERE IS NO `rate` OR `presentDays` FIELD, AND THE SERVER HAS NONE EITHER.
 * `Absence` records absences only: a day when everybody came writes no row, so
 * the denominator a percentage needs does not exist. Inventing one on the client
 * would be worse than inventing it on the server — it would be a number about
 * somebody's child that no test in the backend could ever catch.
 */
export interface AttendanceDay {
  /** `YYYY-MM-DD`. */
  day: string;
}

/**
 * What is known about one day.
 *
 * 🔴 `NO_REGISTER` IS NOT A FAILURE STATE AND MUST KEEP ITS OWN COLOUR. It is
 * every day before registers were recorded, and every day a teacher does not
 * open the screen. Folding it into `PRESENT` would put a green square on a
 * parent's phone for a day nobody checked — which is the single dishonesty this
 * whole screen is arranged to prevent.
 */
export type AttendanceState = "ABSENT" | "PRESENT" | "HOLIDAY" | "NO_REGISTER";

export interface AttendanceDayState {
  /** `YYYY-MM-DD`. */
  day: string;
  state: AttendanceState;
  /** Set only when `state` is `HOLIDAY`. */
  holidayName: string | null;
}

export interface ParentAttendance {
  playerId: string;
  childName: string;
  className: string | null;
  /**
   * 🔴 SO THE DAY DETAIL CAN ASK FOR THIS CHILD'S HOMEWORK AND HAVE THE SERVER
   * SCOPE IT. Homework due on Tuesday is part of "what happened on Tuesday", and
   * `GET /homework` with no `classId` returns every class this parent's children
   * sit in — right for an account and wrong for a calendar with one child on it.
   *
   * ⚠️ OPTIONAL, SO A SERVER THAT PREDATES THIS FIELD DOES NOT BLANK THE SCREEN.
   * The day detail simply shows no homework row, which is what it showed before.
   */
  classId?: string | null;
  windowDays: number;
  /** Newest first. */
  absences: AttendanceDay[];
  absentDays: number;
  /** May be older than the window. Null means never marked absent. */
  lastAbsence: string | null;
  /**
   * Every day in the window, oldest first.
   *
   * 🔴 THE SERVER DECIDES THE STATE, NOT THIS CLIENT. Three tables have to agree
   * — absences, registers and the school's holidays — and the precedence between
   * them (an absence outranks a holiday) is a product decision. A client that
   * received the three lists would be re-implementing that rule, and the first
   * time it drifted a parent would read a different answer from the school's.
   *
   * ⚠️ OPTIONAL, BECAUSE A SERVER THAT PREDATES THIS FIELD MUST NOT BLANK THE
   * SCREEN. The calendar falls back to marking absences only, which is exactly
   * what it drew before.
   */
  days?: AttendanceDayState[];
}

/* ────────────────────────────────────────────────── the per-day activity ── */

/**
 * What one child did, day by day — the read behind C6's day detail and C2's
 * charts.
 *
 * 🔴 ONE ENDPOINT FOR BOTH SCREENS, WHICH IS WHY IT IS ONE TYPE. A calendar tap
 * asks "what happened on Tuesday" and a chart asks "how much per day"; those are
 * the same fold read in two directions, and two endpoints would be two folds
 * that disagree the first time one changes.
 *
 * 🔴 AND IT CARRIES NO PERCENTAGE, EXACTLY LIKE EVERY OTHER PARENT READ. The one
 * ratio the client draws — accuracy — arrives as `correct` and `answered`, so
 * the screen prints the division rather than a number a parent cannot check.
 * Same rule as `ParentDashboard`'s motor section and its `sessionsPlayed`.
 */
export interface ActivityGameRow {
  /** The id the events table records — "sky-quest". `gameArt.ts` owns the name. */
  game: string;
  events: number;
  topLevel: number | null;
}

export interface ActivitySkillRow {
  skill: Skill;
  events: number;
  topLevel: number | null;
}

export interface ActivityDay {
  /** `YYYY-MM-DD`, IST — the same key the attendance calendar's squares carry. */
  day: string;
  sessions: number;
  events: number;
  /**
   * Whole minutes of play.
   *
   * ⚠️ A FLOOR, NOT A MEASUREMENT. A sitting nothing ever closed contributes
   * nothing, so the screen says "at least" wherever it prints this.
   */
  minutes: number;
  correct: number;
  wrong: number;
  /** A level MASTERED — the thing a parent repeats to other people. */
  levelUps: number;
  /** Busiest first. */
  games: ActivityGameRow[];
  /** Busiest first. */
  skills: ActivitySkillRow[];
}

export interface ActivityTotals {
  activeDays: number;
  sessions: number;
  events: number;
  minutes: number;
  correct: number;
  wrong: number;
  /** `correct + wrong`, sent so the client never adds two numbers to divide by. */
  answered: number;
  levelUps: number;
  bestRun: number;
}

export interface ParentActivity {
  playerId: string;
  childName: string;
  /** The window the server actually used — it clamps, and it says which. */
  windowDays: number;
  /** Every day, oldest first, empty days included. */
  days: ActivityDay[];
  totals: ActivityTotals;
  /** True when the read hit its runaway guard. The screen says so rather than
      drawing a total it cannot stand behind. */
  truncated: boolean;
}

export interface ParentNotification {
  id: string;
  /** C4's requirement: each item tagged by channel. */
  channel: "WHATSAPP" | "IN_APP";
  template: string;
  playerId: string | null;
  occurredAt: string;
  sentAt: string | null;
  status: string;
  deliveryStatus: string | null;
}

export interface KindPreference {
  kind: NotificationKind;
  enabled: boolean;
  frequency: NotificationFrequency;
  /** True when the value comes from the account default rather than this child. */
  inherited: boolean;
}

export interface QuietHours {
  /** Minutes past midnight, IST. `quietFrom > quietTo` wraps midnight. */
  quietFrom: number | null;
  quietTo: number | null;
}

export interface AccountPreferences {
  language: "en" | null;
  whatsappOptIn: boolean;
  quietHours: QuietHours | null;
  kinds: KindPreference[];
}

export interface ChildPreferences {
  playerId: string;
  childName: string;
  recordingOptOut: boolean;
  /** P3-31 / D1 — M9 "Use the camera on the school laptop" switched off. */
  cameraOptOut: boolean;
  kinds: KindPreference[];
}

/** C3 reads `GET /progress/:playerId`, which already exists and is parent-scoped. */
/** P3-30 — when a sticker was first known to be earned. */
export interface StickerAward {
  id: string;
  /** ISO instant. */
  earnedAt: string;
}

export interface ProgressSnapshot {
  coins: number;
  stickers: string[];
  streakDays: number;
  lastPlayed: string | null;
  /** P3-30 — the longest streak ever reported. Optional: an older API lacks it. */
  bestStreakDays?: number;
  /**
   * P3-30 — each sticker's earliest known date, earliest first. A sticker in
   * `stickers` with no entry here is shown undated, never given a made-up date.
   */
  awards?: StickerAward[];
}

const get = <T>(path: string, auth: ParentAuth): Promise<T> =>
  request<T>(path, { context: "user", token: auth.token, signal: auth.signal });

const post = <T>(path: string, body: unknown, auth: ParentAuth): Promise<T> =>
  request<T>(path, {
    method: "POST",
    body,
    context: "user",
    token: auth.token,
    signal: auth.signal,
  });

/* ────────────────────────────────────────────────────────────── C1, C4 ── */

export const getChildren = (auth: ParentAuth = {}): Promise<ChildSummary[]> =>
  get("/parent/children", auth);

export const getDashboard = (
  playerId: string,
  auth: ParentAuth = {},
): Promise<ParentDashboard> =>
  get(`/parent/children/${encodeURIComponent(playerId)}/dashboard`, auth);

export const getNotifications = (
  auth: ParentAuth = {},
): Promise<ParentNotification[]> => get("/parent/notifications", auth);

/**
 * C6 — attendance for one child.
 *
 * ⚠️ NOT `GET /classes/:classId/attendance`, which is the teacher's register and
 * carries every child in the section by name and roll number. The parent tier's
 * unit is a child, so the path names one and the server proves it is theirs.
 */
export const getAttendance = (
  playerId: string,
  auth: ParentAuth = {},
  /**
   * How far back to look, in days.
   *
   * ⚠️ A REQUEST, NOT A CONTRACT — the same rule `getActivity` carries. The
   * server clamps it (1–366) and the answer states the window it used, so the
   * screen labels its calendar from `windowDays` in the response rather than
   * from the number it asked with. Omitted, the server's own default (28)
   * stands, which is what every caller got before C6 grew months.
   */
  days?: number,
): Promise<ParentAttendance> =>
  get(
    `/parent/children/${encodeURIComponent(playerId)}/attendance${
      days === undefined ? "" : `?days=${String(days)}`
    }`,
    auth,
  );

/**
 * C6's day detail and C2's charts, from one read.
 *
 * ⚠️ `days` IS A REQUEST, NOT A CONTRACT. The server clamps it (1–120) and the
 * answer states the window it used — so the screen draws `windowDays` from the
 * response rather than the number it asked with. A client that labelled its
 * chart with its own request would mislabel it the day the clamp bites.
 */
export const getActivity = (
  playerId: string,
  days: number,
  auth: ParentAuth = {},
): Promise<ParentActivity> =>
  get(
    `/parent/children/${encodeURIComponent(playerId)}/activity?days=${String(days)}`,
    auth,
  );

/* ────────────────────────────────────────────────────────────────── C2 ── */

/**
 * C2's activity list and its insight.
 *
 * 🔴 THE EXISTING ROUTES, NOT PARENT-SHAPED COPIES. `api.md` §1 marks both
 * *"teacher/parent — scoped"* and `OwnPlayerGuard` already admits a parent for
 * their own child. Duplicating them under `/parent/...` would be two
 * implementations of one read, which is the thing this codebase spends most of
 * its comments preventing.
 */
export interface EventRow {
  id: string;
  game: string;
  type: string;
  skill: string | null;
  level: number | null;
  createdAt: string;
}

export interface Insight {
  summary: string;
  strengths: string[];
  focusAreas: string[];
  generatedBy: string;
}

export const getChildEvents = (
  playerId: string,
  auth: ParentAuth = {},
): Promise<EventRow[]> =>
  get(`/players/${encodeURIComponent(playerId)}/events`, auth);

export const getChildInsight = (
  playerId: string,
  auth: ParentAuth = {},
): Promise<Insight> =>
  get(`/ai/insights/${encodeURIComponent(playerId)}`, auth);

/* ────────────────────────────────────────────────────────────────── C3 ── */

export const getChildProgress = (
  playerId: string,
  auth: ParentAuth = {},
): Promise<ProgressSnapshot> =>
  get(`/progress/${encodeURIComponent(playerId)}`, auth);

/* ────────────────────────────────────────────────────────────────── C5 ── */

export interface AccountPreferencesInput {
  language?: "en" | null;
  whatsappOptIn?: boolean;
  quietHours?: QuietHours | null;
  kinds?: {
    kind: NotificationKind;
    enabled?: boolean;
    frequency?: NotificationFrequency;
  }[];
}

export interface ChildPreferencesInput {
  recordingOptOut?: boolean;
  cameraOptOut?: boolean;
  kinds?: {
    kind: NotificationKind;
    enabled?: boolean;
    frequency?: NotificationFrequency;
  }[];
}

export const getAccountPreferences = (
  auth: ParentAuth = {},
): Promise<AccountPreferences> => get("/preferences/me", auth);

/**
 * ⚠️ SENDS ONLY WHAT CHANGED. The backend schema is all-optional and strict for
 * this reason: C5 is five independent switches, and a body carrying every field
 * would let changing the language clear the quiet hours the parent set last
 * month.
 */
export const saveAccountPreferences = (
  input: AccountPreferencesInput,
  auth: ParentAuth = {},
): Promise<AccountPreferences> => post("/preferences/me", input, auth);

export const getChildPreferences = (
  playerId: string,
  auth: ParentAuth = {},
): Promise<ChildPreferences> =>
  get(`/preferences/players/${encodeURIComponent(playerId)}`, auth);

export const saveChildPreferences = (
  playerId: string,
  input: ChildPreferencesInput,
  auth: ParentAuth = {},
): Promise<ChildPreferences> =>
  post(`/preferences/players/${encodeURIComponent(playerId)}`, input, auth);
